import { Card } from "@/components/ui/card";
import { Check } from "lucide-react";
import { Contact } from "@/components/Contact";

const Pricing = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative">
        {/* Hero Section with Image */}
        <div className="relative h-[300px] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d"
            alt="Pricing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-purple-600/90" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg text-white/90 max-w-2xl">
              Choose the perfect plan for your business needs. No hidden fees, no surprises.
            </p>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10 pb-20">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Basic Plan */}
            <Card className="p-8 bg-white hover:shadow-lg transition-shadow">
              <div className="text-center mb-8">
                <span className="text-blue-600 font-semibold">STARTER</span>
                <h3 className="text-2xl font-bold mb-2 mt-2">Basic</h3>
                <p className="text-4xl font-bold mb-4">$49<span className="text-lg font-normal">/month</span></p>
                <p className="text-gray-600">Perfect for small businesses</p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>Up to 5 users</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>2 Workflows</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>5GB Storage</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>Email Support</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>Basic Analytics</span>
                </li>
              </ul>
              <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                Start Free Trial
              </button>
            </Card>

            {/* Professional Plan */}
            <Card className="p-8 bg-gradient-to-br from-blue-600 to-purple-600 text-white transform hover:scale-105 transition-all duration-200">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
              <div className="text-center mb-8">
                <span className="text-blue-200 font-semibold">PROFESSIONAL</span>
                <h3 className="text-2xl font-bold mb-2 mt-2">Business</h3>
                <p className="text-4xl font-bold mb-4">$99<span className="text-lg font-normal">/month</span></p>
                <p className="text-white/90">For growing teams</p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-2">
                  <Check className="text-white" size={20} />
                  <span>Up to 20 users</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-white" size={20} />
                  <span>Unlimited Workflows</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-white" size={20} />
                  <span>20GB Storage</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-white" size={20} />
                  <span>Priority Support</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-white" size={20} />
                  <span>Advanced Analytics</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-white" size={20} />
                  <span>Custom Integrations</span>
                </li>
              </ul>
              <button className="w-full bg-white text-blue-600 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                Start Free Trial
              </button>
            </Card>

            {/* Enterprise Plan */}
            <Card className="p-8 bg-white hover:shadow-lg transition-shadow">
              <div className="text-center mb-8">
                <span className="text-blue-600 font-semibold">ENTERPRISE</span>
                <h3 className="text-2xl font-bold mb-2 mt-2">Enterprise</h3>
                <p className="text-4xl font-bold mb-4">Custom</p>
                <p className="text-gray-600">For large organizations</p>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>Unlimited Users</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>Unlimited Workflows</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>Unlimited Storage</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>24/7 Premium Support</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>Custom Development</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="text-green-500" size={20} />
                  <span>SLA Guarantee</span>
                </li>
              </ul>
              <button className="w-full bg-gray-900 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors">
                Contact Sales
              </button>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-8">
                <div>
                  <h3 className="font-semibold mb-2">Can I try before I buy?</h3>
                  <p className="text-gray-600">Yes, we offer a 14-day free trial for all our plans. No credit card required.</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">What payment methods do you accept?</h3>
                  <p className="text-gray-600">We accept all major credit cards, PayPal, and bank transfers for enterprise customers.</p>
                </div>
              </div>
              <div className="space-y-8">
                <div>
                  <h3 className="font-semibold mb-2">Can I change plans later?</h3>
                  <p className="text-gray-600">Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">What kind of support do you offer?</h3>
                  <p className="text-gray-600">We offer email support for all plans, with priority support and dedicated account managers for higher tiers.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Need More Information?</h2>
            <Contact />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
